import { useEffect, useMemo, useState } from 'react';
import {
  computeRetryExpiresAt,
  formatCountdown,
  formatRetryAtTime,
  getSecondsUntilRetry,
} from '../utils/rateLimit';

/**
 * Live countdown for rate-limit retry windows.
 * @param {{ retryAfterSeconds?: number | null, expiresAt?: Date | null }} options
 */
export function useRateLimitCountdown({
  retryAfterSeconds = null,
  expiresAt: expiresAtProp = null,
} = {}) {
  const expiresAt = useMemo(() => {
    if (expiresAtProp instanceof Date && !Number.isNaN(expiresAtProp.getTime())) {
      return expiresAtProp;
    }
    return computeRetryExpiresAt(retryAfterSeconds);
  }, [retryAfterSeconds, expiresAtProp]);

  const [secondsRemaining, setSecondsRemaining] = useState(() =>
    getSecondsUntilRetry(expiresAt)
  );

  useEffect(() => {
    setSecondsRemaining(getSecondsUntilRetry(expiresAt));
  }, [expiresAt]);

  useEffect(() => {
    if (secondsRemaining <= 0) {
      return undefined;
    }

    const timer = setInterval(() => {
      setSecondsRemaining(getSecondsUntilRetry(expiresAt));
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAt, secondsRemaining]);

  const canRetry = secondsRemaining <= 0;

  return {
    secondsRemaining,
    canRetry,
    expiresAt,
    countdownLabel: formatCountdown(secondsRemaining),
    retryAtLabel: formatRetryAtTime(expiresAt),
  };
}
